import { Link, Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { useAuth } from './auth.jsx'
import { useRbac } from './lib/rbac.js'
import { CATEGORIES, DEFAULT_CATEGORY_PATH, categoryPath } from './lib/categories.js'
import Shell from './components/Shell.jsx'
import AppLayout from './components/AppLayout.jsx'
import WizardLayout from './components/WizardLayout.jsx'
import Login from './pages/Login.jsx'
import Signup from './pages/Signup.jsx'
import ForgotPassword from './pages/ForgotPassword.jsx'
import Dashboard from './pages/Dashboard.jsx'
import Chat from './pages/Chat.jsx'
import CompanyDirectory from './pages/CompanyDirectory.jsx'
import LeadDetail from './pages/LeadDetail.jsx'
import NewLead from './pages/NewLead.jsx'
import ImportLeads from './pages/ImportLeads.jsx'
import Collaboration from './pages/Collaboration.jsx'
import Activity from './pages/Activity.jsx'
import Sales from './pages/Sales.jsx'
import Software from './pages/Software.jsx'
import Events from './pages/Events.jsx'
import Settings from './pages/Settings.jsx'
import InviteTeam from './pages/InviteTeam.jsx'
import SearchResults from './pages/SearchResults.jsx'

// Public marketing site (revailo-sales.com)
import LandingPage from './marketing/LandingPage.jsx'
import FeaturesPage from './marketing/FeaturesPage.jsx'
import SolutionsPage from './marketing/SolutionsPage.jsx'
import IntegrationsPage from './marketing/IntegrationsPage.jsx'
import SecurityPage from './marketing/SecurityPage.jsx'
import HowItWorksPage from './marketing/HowItWorksPage.jsx'
import CustomersPage from './marketing/CustomersPage.jsx'
import AboutPage from './marketing/AboutPage.jsx'
import CareersPage from './marketing/CareersPage.jsx'
import BlogPage from './marketing/BlogPage.jsx'
import ChangelogPage from './marketing/ChangelogPage.jsx'
import StatusPage from './marketing/StatusPage.jsx'
import PricingPage from './marketing/PricingPage.jsx'
import PrivacyTermsPage from './marketing/PrivacyTermsPage.jsx'
import DpaPage from './marketing/DpaPage.jsx'
import FaqPage from './marketing/FaqPage.jsx'
import ContactSalesPage from './marketing/ContactSalesPage.jsx'

function RequireAuth({ children }) {
  const { user } = useAuth()
  if (!user) return <Navigate to="/login" replace />
  return children
}

// Route-level RBAC: deep links to pages the role can't use render an honest
// in-place notice instead of the page (friendlier than a silent redirect).
function RequirePermission({ perm, children }) {
  const { can, roleLabel } = useRbac()
  if (can(perm)) return children
  // A member never sees role language — no "higher role", no "ask an admin",
  // no hint that a page exists behind a wall. Their app IS chat + leads, so a
  // URL outside it simply lands home, the way an unknown URL would. The notice
  // below is only for cockpit roles (e.g. a manager opening the admin-only
  // audit log), who already live inside the role model.
  if (!can('pages.workspace')) return <Navigate to="/chat" replace />
  const home = '/dashboard'
  return (
    <div className="grid min-h-[60vh] place-items-center p-6">
      <div className="max-w-[420px] rounded-[12px] border border-line bg-surface p-8 text-center shadow-card">
        <div className="text-[15px] font-semibold text-ink">This page needs a higher role</div>
        <p className="m-0 mt-2 text-[14px] leading-[1.55] text-dim">
          You're signed in as <strong className="text-ink">{roleLabel}</strong>, which can't access
          this. Ask a workspace admin if you need it.
        </p>
        <Link
          to={home}
          className="mt-5 inline-flex items-center justify-center rounded-[9px] bg-primary px-[14px] py-[8px] text-[13px] font-semibold text-white no-underline transition-colors hover:bg-primary-dark"
        >
          {home === '/chat' ? 'Back to Chat' : 'Back to Dashboard'}
        </Link>
      </div>
    </div>
  )
}

// Where "/" lands for a signed-in user: the cockpit for roles that hold the
// workspace pages, chat for a member — chat IS their default view. Waits for
// the role to actually resolve (cache or roster) so a member's first-ever
// sign-in doesn't bounce through the dashboard's role notice on the optimistic
// pre-roster default.
function HomeRedirect() {
  const { can, ready } = useRbac()
  if (!ready) return null
  return <Navigate to={can('pages.workspace') ? '/dashboard' : '/chat'} replace />
}

export default function App() {
  const { user } = useAuth()
  return (
    <Routes>
      {/* ---- Home: public landing when signed out; signed in, the role's own
           home — dashboard for leadership, chat for a member. ---- */}
      <Route path="/" element={user ? <HomeRedirect /> : <LandingPage />} />

      {/* ---- Auth ---- */}
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/signup" element={user ? <Navigate to="/" replace /> : <Signup />} />
      <Route
        path="/forgot-password"
        element={user ? <Navigate to="/" replace /> : <ForgotPassword />}
      />

      {/* ---- Public marketing pages (viewable signed in or out) ---- */}
      <Route path="/overview" element={<LandingPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/solutions" element={<SolutionsPage />} />
      <Route path="/integrations" element={<IntegrationsPage />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/customers" element={<CustomersPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/changelog" element={<ChangelogPage />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/privacy" element={<PrivacyTermsPage />} />
      <Route path="/terms" element={<PrivacyTermsPage />} />
      <Route path="/dpa" element={<DpaPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/contact" element={<ContactSalesPage />} />
      <Route path="/demo" element={<ContactSalesPage />} />

      {/* ---- Signed-in shell: one persistent sidebar shared across the app
              chrome and the full-screen wizards, so the nav's sliding
              indicator animates across the app/wizard boundary instead of
              snapping (the sidebar no longer remounts when the inner layout
              swaps). ---- */}
      <Route
        element={
          <RequireAuth>
            <Shell />
          </RequireAuth>
        }
      >
        {/* Full-screen wizards — bare, edge-to-edge inner layout. */}
        <Route element={<WizardLayout />}>
          <Route path="/leads/new" element={<NewLead />} />
          <Route path="/leads/import" element={<RequirePermission perm="leads.import"><ImportLeads /></RequirePermission>} />
          <Route path="/collaboration/invite" element={<RequirePermission perm="members.invite"><InviteTeam /></RequirePermission>} />
        </Route>

        {/* Signed-in app — topbar + padded content inner layout. */}
        <Route element={<AppLayout />}>
          {/* ── leadership cockpit: the dashboard and the performance pages.
              One pathless guard so a member's deep link to ANY of these lands
              on the honest role notice (with a Back-to-Chat), never the page.
              The sidebar hides the same set via the same permission. */}
          <Route
            element={
              <RequirePermission perm="pages.workspace">
                <Outlet />
              </RequirePermission>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/performance/activity" element={<Activity />} />
            <Route path="/performance/sales" element={<Sales />} />
            <Route path="/performance/software" element={<Software />} />
          </Route>

        {/* Optional param on ONE route: /chat and /chat/<id> render the same
            mounted component, so switching threads never remounts (and never
            aborts a stream in flight). Bare /chat is always a NEW chat. */}
        <Route path="/chat/:convoId?" element={<Chat />} />
        <Route path="/search" element={<SearchResults />} />
        <Route path="/data" element={<Navigate to={DEFAULT_CATEGORY_PATH} replace />} />
        <Route path="/data/opportunities" element={<Navigate to={DEFAULT_CATEGORY_PATH} replace />} />
        <Route path="/data/regions" element={<Navigate to={DEFAULT_CATEGORY_PATH} replace />} />
        <Route path="/data/market-map" element={<Navigate to={DEFAULT_CATEGORY_PATH} replace />} />
        <Route path="/data/segments" element={<Navigate to={DEFAULT_CATEGORY_PATH} replace />} />
        <Route path="/data/segments/:id" element={<Navigate to={DEFAULT_CATEGORY_PATH} replace />} />
        <Route path="/data/intelligence" element={<Navigate to={DEFAULT_CATEGORY_PATH} replace />} />
        {/* One directory route per vertical in lib/categories.js. */}
        {CATEGORIES.map((c) => (
          <Route
            key={c.key}
            path={categoryPath(c.key)}
            element={
              <CompanyDirectory
                category={c.key}
                title={c.plural}
                subtitle={c.subtitle}
              />
            }
          />
        ))}
        <Route path="/leads/company/:id" element={<LeadDetail />} />
        {/* Team management now lives in Collaboration. */}
        <Route path="/performance/team" element={<Navigate to="/collaboration" replace />} />
        <Route path="/logs/events" element={<RequirePermission perm="events.view"><Events /></RequirePermission>} />
          <Route path="/collaboration" element={<Collaboration />} />
          <Route path="/settings" element={<Settings />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
